<template>
  <div class="survey-form">
    <h1 class="survey-form__title">Soft skills survey</h1>

    <SurveyProgress :progress="progress" />

    <h2 v-if="surveyStep.title" class="survey-form__subtitle">{{ surveyStep.title }}</h2>

    <SurveyAllert v-if="surveyStep.description" :content="surveyStep.description" />

    <div class="survey-form__questions">
      <v-slide-x-transition group mode="out-in">
        <question-container
          v-for="(question, index) in surveyStep.questions"
          :key="question.id"
          :index="question.details.display_number"
          :title="question.title"
          :uptitle="question.metatitle"
          :description="question.description"
        >
          <component
            v-model="answers[index]"
            :is="questionComponentType(question.type)"
            :id="question.id"
            :parameters="parametersOf(question)"
          />
        </question-container>
      </v-slide-x-transition>
    </div>

    <div v-if="last && !isRegister" class="accept-terms">
      <v-checkbox
        v-model="sensitive_data_accepted"
        class="accept-terms__checkbox"
        color="blue"
        dense
        hide-details
      >
        <template #label>
          <span class="accept-terms__acceptance">
            I want to take part in the recruitment conducted by Applover Software House
            based in Wrocław, ul. Świeradowska 77, 50-599 and I consent to the processing
            of my data other than those regulated by law.<br>
            I can withdraw my consent at any time, but actions performed prior to its
            withdrawal will remain legal.
          </span>
        </template>
      </v-checkbox>
    </div>

    <div class="survey-form__action">
      <v-btn
        color="blue"
        class="white--text"
        x-large
        :disabled="surveyDisabled"
        :loading="processing"
        :min-width="255"
        @click="submitStep">
        {{ isRegister && last ? "Finish" : "Continue" }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import SurveyProgress from '@/components/Survey/SurveyProgress.vue';
import SurveyAllert from '@/components/Survey/SurveyAllert.vue';
import QuestionContainer from '@/components/Survey/QuestionContainer.vue';
import QuestionSelect from '@/components/Survey/QuestionSelect.vue';
import QuestionTipi from '@/components/Survey/QuestionTipi.vue';
import QuestionInput from '@/components/Survey/QuestionInput.vue';
import QuestionScale from '@/components/Survey/QuestionScale.vue';
import QuestionSort from '@/components/Survey/QuestionSort.vue';

const paramExtractors = {
  single_select: (question) => ({ possibleAnswers: question.possible_answers }),
  tipi: (question) => ({ possibleAnswers: question.possible_answers }),
  scale: (question) => ({
    possibleAnswers: question.possible_answers,
    details: question.details,
  }),
  order: (question) => ({ possibleAnswers: question.possible_answers }),
  default: () => {},
};

export default {
  components: {
    SurveyProgress,
    SurveyAllert,
    QuestionContainer,
    QuestionSelect,
    QuestionTipi,
    QuestionInput,
    QuestionScale,
    QuestionSort,
  },

  props: {
    surveyStep: {
      type: Object,
      required: true,
    },
    progress: {
      type: Number,
      required: true,
    },
    processing: {
      type: Boolean,
      required: true,
    },
    last: {
      type: Boolean,
      required: true,
    },
    isRegister: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      answers: [],
      sensitive_data_accepted: false,
    };
  },

  computed: {
    questionsNotFilled() {
      return this.answers.length < this.surveyStep.questions.length
        || this.answers.includes(undefined);
    },

    surveyDisabled() {
      return (this.questionsNotFilled)
      || (!this.isRegister && this.last && !this.sensitive_data_accepted);
    },
  },

  watch: {
    surveyStep() {
      this.answers = [];
    },
  },

  methods: {
    questionComponentType(type) {
      switch (type) {
        case 'single_select':
          return 'QuestionSelect';
        case 'tipi':
          return 'QuestionTipi';
        case 'scale':
          return 'QuestionScale';
        case 'order':
          return 'QuestionSort';
        default:
          return 'QuestionInput';
      }
    },

    parametersOf(question) {
      const paramExtractor = paramExtractors[question.type] || paramExtractors.default;
      return paramExtractor(question);
    },

    submitStep() {
      if (!this.questionsNotFilled) {
        this.$emit('submit', this.answers);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.survey-form {
  background: #FFFFFF;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;
  max-width: 636px;
  margin: 0 auto;
  padding: 24px;

  &__title {
    color: $main--headers;
    font-size: 28px;
    margin-bottom: 24px;
  }

  &__subtitle {
    color: $color--primary;
    margin-bottom: 24px;
  }

  &__questions {
    margin-bottom: 0;
    margin-top: 32px;
  }

  &__action {
    margin-top: 24px;
    text-align: right;
  }
}

.accept-terms {
  color: $color--primary;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: flex-start;

  &__checkbox {
    margin-top: 0;
    padding-top: 0px;
  }

  &__acceptance {
    color: $color--primary;
    font-size: 12px;
    line-height: 22px;
    margin-bottom: 12px;
  }

  &__link {
    line-height: 24px;
  }
}
</style>
